/**
 * Pokemon data utilities for the dashboard.
 * Provides search, image URLs, and type info for the Pokemon config UI.
 */

import imageUrls from './image_urls.json';
import variantPokemon from './variant_pokemon.json';

const VARIANT_MAP = variantPokemon as Record<string, { displayName: string; baseName: string }>;

// Pokemon types and their colors
export const POKEMON_TYPES: Record<string, { color: string; emoji: string }> = {
  normal: { color: '#A8A878', emoji: '⚪' },
  fire: { color: '#F08030', emoji: '🔥' },
  water: { color: '#6890F0', emoji: '💧' },
  grass: { color: '#78C850', emoji: '🌿' },
  electric: { color: '#F8D030', emoji: '⚡' },
  ice: { color: '#98D8D8', emoji: '❄️' },
  fighting: { color: '#C03028', emoji: '🥊' },
  poison: { color: '#A040A0', emoji: '☠️' },
  ground: { color: '#E0C068', emoji: '🏔️' },
  flying: { color: '#A890F0', emoji: '🦅' },
  psychic: { color: '#F85888', emoji: '🔮' },
  bug: { color: '#A8B820', emoji: '🐛' },
  rock: { color: '#B8A038', emoji: '🪨' },
  ghost: { color: '#705898', emoji: '👻' },
  dragon: { color: '#7038F8', emoji: '🐉' },
  dark: { color: '#705848', emoji: '🌑' },
  steel: { color: '#B8B8D0', emoji: '⚙️' },
  fairy: { color: '#EE99AC', emoji: '✨' },
};

// Quest regions
export const QUEST_REGIONS = [
  'Kanto', 'Johto', 'Hoenn', 'Sinnoh', 'Unova',
  'Kalos', 'Alola', 'Galar', 'Hisui', 'Paldea',
];

export interface PokemonEntry {
  id: number;
  name: string;
  displayName: string;
  imageUrl: string | null;
}

// Build the pokemon list from image_urls.json keys + known names
const baseList: PokemonEntry[] = Object.keys(imageUrls as Record<string, string>).map((name, i) => ({
  id: i + 1,
  name: name.toLowerCase(),
  displayName: name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' '),
  imageUrl: (imageUrls as Record<string, string>)[name] || null,
}));

// Custom/event variant Pokémon (e.g. "peakychu" -> base species "pikachu")
// aren't in the base PokeAPI-derived list, so they're searchable/renderable too.
const variantList: PokemonEntry[] = Object.entries(VARIANT_MAP).map(([slug, v], i) => ({
  id: baseList.length + i + 1,
  name: slug,
  displayName: v.displayName,
  imageUrl: (imageUrls as Record<string, string>)[v.baseName] || null,
}));

const pokemonList: PokemonEntry[] = [...baseList, ...variantList];

/**
 * Search pokemon by name. Returns up to `limit` results.
 */
export function searchPokemon(query: string, limit = 20): PokemonEntry[] {
  if (!query.trim()) return pokemonList.slice(0, limit);
  const q = query.toLowerCase().trim();
  const results: PokemonEntry[] = [];

  // Exact match first
  for (const p of pokemonList) {
    if (p.name === q) {
      results.push(p);
      break;
    }
  }

  // Starts-with matches
  for (const p of pokemonList) {
    if (results.length >= limit) break;
    if (p.name.startsWith(q) && !results.includes(p)) {
      results.push(p);
    }
  }

  // Contains matches
  for (const p of pokemonList) {
    if (results.length >= limit) break;
    if (p.name.includes(q) && !results.includes(p)) {
      results.push(p);
    }
  }

  return results;
}

/**
 * Get a Pokemon by exact name.
 */
export function getPokemon(name: string): PokemonEntry | undefined {
  const n = name.toLowerCase().trim();
  return pokemonList.find((p) => p.name === n);
}

/**
 * Get the Cloudinary image URL for a Pokemon name.
 */
export function getPokemonImageUrl(name: string): string | null {
  const n = name.toLowerCase().trim();
  return (imageUrls as Record<string, string>)[n] || null;
}

/**
 * Small emoji-style sprite URL for a Pokémon name.
 * Uses PokeAPI's sprites repo — works for base forms by name slug.
 * For variants, falls back to a type emoji.
 */
export function getPokemonSpriteUrl(name: string): string {
  const n = name.toLowerCase().trim();
  // Custom/event variants (e.g. "peakychu") map to a real base species —
  // fall back to that species' sprite since the variant itself has none.
  const variant = VARIANT_MAP[n];
  if (variant) return `https://img.pokemondb.net/sprites/sword-shield/icon/${variant.baseName}.png`;
  // PokeAPI sprites use name slugs for most pokemon
  // For variants (mega, alola, galar, etc.), the base form sprite is good enough visually
  const base = n.replace(/-mega.*/, '').replace(/-alola.*/, '').replace(/-galar.*/, '').replace(/-hisui.*/, '').replace(/-paldea.*/, '').replace(/-gmax.*/, '').replace(/-totem.*/, '').replace(/-starter.*/, '').replace(/-cap.*/, '').replace(/-cosplay.*/, '').replace(/-rock-star.*/, '').replace(/-pop-star.*/, '').replace(/-belle.*/, '').replace(/-phd.*/, '').replace(/-libre.*/, '').replace(/-original.*/, '').replace(/-partner.*/, '').replace(/-world.*/, '').replace(/-primal.*/, '').replace(/-origin.*/, '').replace(/-dusk.*/, '').replace(/-own-tempo.*/, '');
  return `https://img.pokemondb.net/sprites/sword-shield/icon/${base}.png`;
}

/**
 * Total count of pokemon in the dataset.
 */
export const POKEMON_COUNT = pokemonList.length;
